import { get } from 'lodash';
import axios from 'axios';
import template from 'url-template';
import { Auth } from 'aws-amplify';
import { handleNetworkError, enqueueSnackbar } from './applicationMessageActions';
import { dataUrlToBlob } from '../utils/blob-util';

import awsHeader from '../helpers/awsAuth';
import k2aBaseApi from '../apis/key2act';
import lambdaBaseApi from '../apis/lambda';

import * as ActionTypes from './actionTypes';

import { initUser } from './currentUserActions';
import { fetchCompanyInfo } from './companyInfoActions';

const AuthorizationUrl = template.parse('/companies/{companyId}/authorization-roles');
const CompaniesUrl = template.parse('/companies');
const MyCompaniesUrl = template.parse('/me/companies{?page,limit}');
const MySettingsUrl = template.parse('/me/settings');
const UpdateCompanyUrl = template.parse('/companies/{companyId}');
const ImportUrl = template.parse('/storage/companyLogo');
const AnnouncementUrlwithId = template.parse('/announcements/{announcementId}');
const apiVersionUrl = template.parse('/public/apiversion');

const DEFAULT_PAGE_LIMIT = 200;

const doFileImport = async (file, header) => {
    try {
        const fileImportResponse = await lambdaBaseApi.put(
            ImportUrl.expand(),
            {},
            header
        );

        const blob = await dataUrlToBlob(file);

        await axios({
            method:  'put',
            url:     fileImportResponse.data.url,
            data:    blob,
            headers: {
                'Content-Type': 'image/png'
            }
        });
    } catch (err) {
        throw new Error(err);
    }
};

export const createCompany = (body, options = {}) => async dispatch => {
    const header = await awsHeader();

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);
    options.page || (options.page = 1);

    let postPayload;
    try {
        try {
            const postResponse = await k2aBaseApi.post(
                CompaniesUrl.expand(),
                body,
                header
            );

            postPayload = get(postResponse, 'data.id', '');

            dispatch(enqueueSnackbar('Company created successfully.', 'success'));
        } catch (error) {
            dispatch(enqueueSnackbar('Network Error - Company not created.', 'error'));

            throw new Error(error);
        }

        try {
            if (body.fileImport) {
                // Creating a new instance of the headers because we are manipulating the K2A-Company
                // property to reflect the newly created company. This is non-standard and should
                // only be modified with care.
                const fileImportHeader = await awsHeader();
                fileImportHeader.headers['K2A-Company'] = postPayload;

                await doFileImport(body.fileImport, fileImportHeader);

                // Giving the server 5 seconds to process file upload before GET'ting the companies
                await _timeout(5000);
            }
        } catch (error) {
            dispatch(enqueueSnackbar('Network Error - Company Logo not uploaded.', 'error'));

            throw new Error(error);
        }

        try {
            const response = await k2aBaseApi.get(
                MyCompaniesUrl.expand({
                    ...options
                }),
                header
            );
            const payload = get(response, 'data.data', []);

            dispatch({
                type: ActionTypes.FETCH_COMPANIES,
                payload
            });
        } catch (error) {
            dispatch(enqueueSnackbar('Network Error - Companies could not be retrieved.', 'error'));

            throw new Error(error);
        }

        return true;
    } catch (error) {
        return false;
    }
};

export const fetchCompanies = (options = {}) => async (dispatch,getState) => {
    const header = await awsHeader();

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);
    options.page || (options.page = 1);

    try {
        const response = await k2aBaseApi.get(
            MyCompaniesUrl.expand({
                ...options
            }),
            header
        );
        const payload = get(response, 'data.data', []);
        const announcements = get(response, 'data.activeAnnouncements', []);

        const user = { ...getState().currentUser };

        user.isSuperUser = get(response, 'data.isSuperUser') || false;

        dispatch({
            type:    ActionTypes.SET_CURRENT_USER,
            payload: user
        });

        dispatch({
            type: ActionTypes.FETCH_COMPANIES,
            payload
        });

        if (announcements && announcements.length>0) {   
            const { attributes } = await Auth.currentUserInfo();
            const userId = attributes.email;
            const storgeKey = 'announcements - '+ userId;
            let anns = announcements;   
            let _announcements = window.localStorage.getItem(storgeKey);
            if (_announcements) {
                _announcements =  JSON.parse(_announcements);
                if (_announcements.length>0) {
                    anns = announcements.filter(o=> !_announcements.some(i=> i.id === o.id));
                }
            }
            window.localStorage.setItem(storgeKey,JSON.stringify(announcements));
            if (anns.length>0) {
                //let announcementId = '67ad121e-992a-4bce-8b93-a05a0098c46b';
                let announcementId = anns[0].id;
                const response = await k2aBaseApi.get(
                    AnnouncementUrlwithId.expand({
                        announcementId
                    }),
                    header
                );
                const payload = get(response, 'data', []);
    
                dispatch({
                    type: ActionTypes.FETCH_ANNOUNCEMENT_MSG,
                    payload
                });
            }
        }
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};


export const fetchAllScopes = (companyId, options = {}) => async dispatch => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(
            AuthorizationUrl.expand({
                companyId,
                ...options
            }),
            header
        );

        const scopes = get(response, 'data.data', []);
        const adminScopes = scopes.find(({ name }) => name === 'Admin');
        const payload = get(adminScopes, 'scopes', []);

        dispatch({
            type: ActionTypes.FETCH_ALL_SCOPES,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchDefaultCompany = (options = {}) => async (dispatch, getState) => {
    const currentUser = getState().currentUser;
    const companies = getState().core.companies;

    const payload = companies.find(company => {
        return company.company.id === currentUser.defaultCompany;
    });

    dispatch({
        type: ActionTypes.FETCH_DEFAULT_COMPANY,
        payload
    });
};

export const fetchSwaggerDocumentation = (options = {}) => async dispatch => {
    let env = process.env.REACT_APP_STAGE;

    if (env === 'stage') {
        env = 'uat';
    }

    options = {
        headers: {
            'Content-Type':                 'application/json',
            'Access-Control-Allow-Origin':  '*',
            'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, PATCH, OPTIONS',
            'Access-Control-Allow-Headers': 'X-Requested-With, content-type, Authorization'
        }
    };
    try {
        const response = await axios.get(`https://combined-swagger-${env}.s3.amazonaws.com/combinedSwagger.json`, options);

        return response.data;

    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const setCurrentApp = app => ({
    type:    ActionTypes.FETCH_CURRENT_APP,
    payload: app
});

export const setDefaultCompany = body => async dispatch => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.put(
            MySettingsUrl.expand(),
            body,
            header
        );

        await dispatch(initUser());

        await dispatch(fetchCompanies());
        await dispatch(fetchDefaultCompany());
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const setAllRoles = companies => {
    const roles = companies.map(company => ({
        companyName: company.company.name,
        companyId:   company.company.id,
        roleName:    company.role ? company.role.name : '',
        roleId:      company.role ? company.role.id : '',
        scopes:      company.role ? [...company.role.scopes] : []
    }));

    return {
        type:    ActionTypes.SET_ALL_ROLES,
        payload: roles
    };
};

export const setCurrentScopes = scopes => ({
    type:    ActionTypes.SET_CURRENT_SCOPES,
    payload: scopes
});

export const setProgressBarLoading = payload => ({
    type: ActionTypes.SET_PROGRESS_BAR_LOADING,
    payload
});

export const setBootstrapping = payload => ({
    type: ActionTypes.SET_BOOTSTRAPPING,
    payload
});

export const updateCompany = (companyId, body, options = {}) => async dispatch => {
    const header = await awsHeader();

    options.limit || (options.limit = DEFAULT_PAGE_LIMIT);
    options.page || (options.page = 1);

    try {
        await k2aBaseApi.put(
            UpdateCompanyUrl.expand({
                companyId
            }),
            body,
            header
        );

        if (body.fileImport) await doFileImport(body.fileImport, header);

        const response = await k2aBaseApi.get(
            MyCompaniesUrl.expand({
                ...options
            }),
            header
        );
        const payload = get(response, 'data.data', []);

        const targetCompany = payload.find(target => {
            return target.company.id === companyId;
        });

        await dispatch(fetchCompanyInfo(targetCompany));

        dispatch({
            type: ActionTypes.FETCH_COMPANIES,
            payload
        });

        dispatch({
            type:    ActionTypes.TOGGLE_LOADING,
            payload: false
        });

        dispatch(enqueueSnackbar('Company updated successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};

const _timeout = ms => {
    return new Promise(resolve => setTimeout(resolve, ms));
};

export const fetchApiVersion = (options ={}) => async(dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(
            apiVersionUrl.expand({
                ...options
            }),
            header
        );

        const payload = get(response, 'data','');

        if (payload) {
            let deployedBranch = payload.deployedBranch;

            if (deployedBranch === 'master') {
                deployedBranch = 'Development';
            }
            else {
                deployedBranch = deployedBranch.substring(deployedBranch.indexOf('/') + 1).replace('-', '.');
            }

            dispatch({
                type:    ActionTypes.FETCH_API_VERSION,
                payload: deployedBranch
            });
        }
    }
    catch (error) {
        dispatch(handleNetworkError(error));
    }
};
